import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { PlaidLinkButton } from '../components/PlaidLinkButton';
import { Button } from '../components/ui/button';
import { ArrowLeft, CheckCircle2, ChevronLeft, ChevronRight, Sparkles, Wand2, BarChart3, Mail, Shield } from 'lucide-react';

type Step = 1 | 2 | 3 | 4;

const clampStep = (n: number): Step => {
  if (n <= 1) return 1;
  if (n >= 4) return 4;
  return n as Step;
};

export const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const { profile, refreshAuth } = useAuth();

  const [step, setStep] = useState<Step>(1);
  const [isFinishing, setIsFinishing] = useState(false);
  const [hasConnectedBank, setHasConnectedBank] = useState(false);
  const [loadingIntegrations, setLoadingIntegrations] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSecurityDetails, setShowSecurityDetails] = useState(false);

  const onboardingCompleted = !!profile?.onboarding_completed;

  // If the user already completed onboarding, bounce them out.
  useEffect(() => {
    if (onboardingCompleted) {
      navigate('/', { replace: true });
    }
  }, [onboardingCompleted, navigate]);

  // Detect if user already has an active Plaid integration, so Step 2 can be satisfied.
  useEffect(() => {
    const abort = new AbortController();
    const run = async () => {
      setLoadingIntegrations(true);
      setError(null);
      try {
        const resp = await apiService.getPlaidIntegrationsOnce({ signal: abort.signal });
        if (!abort.signal.aborted) {
          const active = (resp.integrations || []).some((i) => i.status === 'active');
          setHasConnectedBank(active);
        }
      } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') return;
        // Non-blocking: user can still try linking.
      } finally {
        if (!abort.signal.aborted) setLoadingIntegrations(false);
      }
    };
    run();
    return () => abort.abort();
  }, []);

  const canGoNext = useMemo(() => {
    if (step === 2) return hasConnectedBank;
    return true;
  }, [step, hasConnectedBank]);

  const next = () => setStep((s) => clampStep(s + 1));
  const prev = () => setStep((s) => clampStep(s - 1));

  const handleFinish = async () => {
    setIsFinishing(true);
    setError(null);
    try {
      await apiService.updateOnboardingStatus(true);
      await refreshAuth();
      navigate('/', { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to finish onboarding');
    } finally {
      setIsFinishing(false);
    }
  };

  const stepTitle = useMemo(() => {
    switch (step) {
      case 1:
        return 'Why we built Alto';
      case 2:
        return 'Connect your bank';
      case 3:
        return 'Review and edit transactions';
      case 4:
        return 'Unlock insights and Alto AI';
      default:
        return 'Onboarding';
    }
  }, [step]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--theme-background)' }}>
      {/* Top bar */}
      <div className="px-4 py-4 md:px-8 border-b" style={{ borderColor: 'color-mix(in srgb, var(--theme-text-secondary) 12%, transparent)' }}>
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-3">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-sm font-medium"
            style={{ color: 'var(--theme-text-secondary)' }}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </button>

          <div className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
            {stepTitle}
          </div>

          <div className="text-xs font-semibold px-2 py-1 rounded-full"
               style={{
                 backgroundColor: 'color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)',
                 color: 'var(--theme-text-secondary)',
               }}
          >
            Step {step} / 4
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-6 md:px-8 md:py-10">
        <div className="max-w-4xl mx-auto">
          {error && (
            <div
              className="apple-card mb-4 !p-4"
              style={{
                backgroundColor: 'color-mix(in srgb, var(--theme-expense) 10%, var(--theme-surface))',
                borderColor: 'color-mix(in srgb, var(--theme-expense) 30%, transparent)',
              }}
            >
              <p className="text-sm font-medium" style={{ color: 'var(--theme-expense)' }}>
                {error}
              </p>
            </div>
          )}

          <div className="apple-card overflow-hidden">
            <div className="p-5 md:p-8">
              {/* Step indicator */}
              <div className="grid grid-cols-4 gap-2 mb-6">
                {[1, 2, 3, 4].map((n) => {
                  const active = step === n;
                  const done = step > n;
                  return (
                    <div key={n} className="flex flex-col gap-1">
                      <div
                        className="h-2 rounded-full"
                        style={{
                          backgroundColor: done || active
                            ? 'var(--theme-primary)'
                            : 'color-mix(in srgb, var(--theme-text-secondary) 14%, transparent)',
                          opacity: done ? 0.55 : 1,
                        }}
                      />
                      <div className="text-[10px] font-semibold" style={{ color: active ? 'var(--theme-text)' : 'var(--theme-text-secondary)' }}>
                        {n}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Step content */}
              {step === 1 && (
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div
                      className="p-3 rounded-2xl"
                      style={{
                        backgroundColor: 'color-mix(in srgb, var(--theme-primary) 12%, transparent)',
                      }}
                    >
                      <Sparkles className="h-5 w-5" style={{ color: 'var(--theme-primary)' }} />
                    </div>
                    <div>
                      <h1 className="text-xl md:text-2xl font-bold" style={{ color: 'var(--theme-text)' }}>
                        Why we built Alto
                      </h1>
                    </div>
                  </div>

                  <div className="mt-1">
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--theme-text-secondary)' }}>
                      We built Alto because staying on top of spending felt harder than it needed to be. Every tool we
                      tried asked for hours of manual work each month just to answer a simple question like how much
                      did I spend on groceries.
                    </p>
                    <br/>
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--theme-text-secondary)' }}>
                      Alto flips that experience. It automatically understands your
                      transactions using advancements in the AI space, learns from your spending patterns, tracks your spending in real time, and surfaces insights without rules,
                      spreadsheets, or constant cleanup. In the next three steps, you’ll see how Alto connects your
                      accounts, makes sense of your spending for you, and gives you a clear picture of how you’re doing
                      without the tedious work.
                    </p>
                  </div>

                  <div className="pt-2">
                    <p className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
                      Here is how Alto works in three easy steps
                    </p>
                  </div>

                  <div className="grid md:grid-cols-3 gap-3">
                    <div className="apple-card !p-4">
                      <p className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
                        1) Connect your bank
                      </p>
                      <p className="text-xs mt-1" style={{ color: 'var(--theme-text-secondary)' }}>
                        Securely connect your accounts so Alto can sync your transactions automatically.
                      </p>
                    </div>
                    <div className="apple-card !p-4">
                      <p className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
                        2) Alto AI categorizes for you
                      </p>
                      <p className="text-xs mt-1" style={{ color: 'var(--theme-text-secondary)' }}>
                        Alto AI automatically categorizes expenses so you don’t have to.
                      </p>
                    </div>
                    <div className="apple-card !p-4">
                      <p className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
                        3) Get real-time insights
                      </p>
                      <p className="text-xs mt-1" style={{ color: 'var(--theme-text-secondary)' }}>
                        Review analytics and ask Alto AI how your spending is going anytime.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div
                      className="p-3 rounded-2xl"
                      style={{
                        backgroundColor: 'color-mix(in srgb, var(--theme-primary) 12%, transparent)',
                      }}
                    >
                      <Wand2 className="h-5 w-5" style={{ color: 'var(--theme-primary)' }} />
                    </div>
                    <div>
                      <h2 className="text-xl md:text-2xl font-bold" style={{ color: 'var(--theme-text)' }}>
                        Connect your bank accounts
                      </h2>
                      <p className="text-sm mt-1" style={{ color: 'var(--theme-text-secondary)' }}>
                        After you connect, Alto automatically starts syncing your latest <span className="font-semibold">90 days</span> of transactions.
                      </p>
                    </div>
                  </div>

                  <div className="apple-card !p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle2
                        className="h-5 w-5"
                        style={{ color: hasConnectedBank ? 'var(--theme-income)' : 'var(--theme-text-secondary)' }}
                      />
                      <div>
                        <div className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
                          Bank connection
                        </div>
                        <div className="text-xs" style={{ color: 'var(--theme-text-secondary)' }}>
                          {loadingIntegrations
                            ? 'Checking your connection status…'
                            : hasConnectedBank
                              ? 'Connected'
                              : 'Not connected yet'}
                        </div>
                      </div>
                    </div>

                    <PlaidLinkButton
                      onSuccess={() => setHasConnectedBank(true)}
                      onError={(msg) => setError(msg)}
                      variant="default"
                      size="sm"
                      className="rounded-xl bg-[var(--theme-primary)]"
                    >
                      {hasConnectedBank ? 'Connect another bank' : 'Connect bank account'}
                    </PlaidLinkButton>
                  </div>

                  {/* Security & privacy details */}
                  <div className="apple-card !p-0 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setShowSecurityDetails((v) => !v)}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
                    >
                      <div className="flex items-center gap-3">
                        <Shield className="h-5 w-5" style={{ color: 'var(--theme-text-secondary)' }} />
                        <div>
                          <div className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
                            Security & privacy
                          </div>
                          <div className="text-xs" style={{ color: 'var(--theme-text-secondary)' }}>
                            How Alto keeps your data safe
                          </div>
                        </div>
                      </div>
                      <ChevronRight
                        className="h-5 w-5 transition-transform"
                        style={{
                          color: 'var(--theme-text-secondary)',
                          transform: showSecurityDetails ? 'rotate(90deg)' : 'rotate(0deg)',
                        }}
                      />
                    </button>

                    {showSecurityDetails && (
                      <div
                        className="px-4 pb-4 pt-4 border-t"
                        style={{ borderColor: 'color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)' }}
                      >
                        <div className="grid gap-3">
                          <div>
                            <div className="text-xs font-semibold" style={{ color: 'var(--theme-text)' }}>
                              Data at rest
                            </div>
                            <div className="text-xs mt-1" style={{ color: 'var(--theme-text-secondary)' }}>
                              Your data is securely stored using industry-standard AES-256 encryption, the same level of
                              protection used by major financial institutions.
                            </div>
                          </div>

                          <div>
                            <div className="text-xs font-semibold" style={{ color: 'var(--theme-text)' }}>
                              Data in transit
                            </div>
                            <div className="text-xs mt-1" style={{ color: 'var(--theme-text-secondary)' }}>
                              All data is encrypted as it moves between your device and Alto, so your information stays
                              private every step of the way.
                            </div>
                          </div>

                          <div>
                            <div className="text-xs font-semibold" style={{ color: 'var(--theme-text)' }}>
                              AI models
                            </div>
                            <div className="text-xs mt-1" style={{ color: 'var(--theme-text-secondary)' }}>
                              We work only with trusted North American AI providers that meet top-tier security
                              standards and never train their models on your data.
                            </div>
                          </div>

                          <div>
                            <div className="text-xs font-semibold" style={{ color: 'var(--theme-text)' }}>
                              How we use your data
                            </div>
                            <div className="text-xs mt-1" style={{ color: 'var(--theme-text-secondary)' }}>
                              Your data is yours. We never sell it, share it, or expose it to anyone else. It’s only
                              visible and accessible to you.
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {!hasConnectedBank && (
                    <p className="text-xs" style={{ color: 'var(--theme-text-secondary)' }}>
                      To continue, connect at least one bank account.
                    </p>
                  )}
                </div>
              )}

              {step === 3 && (
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div
                      className="p-3 rounded-2xl"
                      style={{
                        backgroundColor: 'color-mix(in srgb, var(--theme-primary) 12%, transparent)',
                      }}
                    >
                      <ChevronRight className="h-5 w-5" style={{ color: 'var(--theme-primary)' }} />
                    </div>
                    <div>
                      <h2 className="text-xl md:text-2xl font-bold" style={{ color: 'var(--theme-text)' }}>
                        Alto will sync and categorize your transactions
                      </h2>
                    </div>
                    
                  </div>

                  <div className="mt-3">
                        <p className="text-sm leading-relaxed" style={{ color: 'var(--theme-text-secondary)' }}>
                          Alto categorizes all of your transactions across all accounts into three categories: expenses,
                          income, and transfers. Today, Alto uses merchant normalization technology and AI that learns
                          from your past transaction coding, and it also uses the latest LLM technology to best determine
                          how every transaction is categorized.
                        </p>
                      </div>

                  <div className="grid md:grid-cols-3 gap-3">
                    <div className="apple-card !p-4">
                      <p className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
                        Expenses
                      </p>
                      <p className="text-xs mt-1" style={{ color: 'var(--theme-text-secondary)' }}>
                        Categorize spending (e.g., groceries, subscriptions, dining).
                      </p>
                    </div>
                    <div className="apple-card !p-4">
                      <p className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
                        Income
                      </p>
                      <p className="text-xs mt-1" style={{ color: 'var(--theme-text-secondary)' }}>
                        Separate paychecks, reimbursements, refunds, and more.
                      </p>
                    </div>
                    <div className="apple-card !p-4">
                      <p className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
                        Transfers
                      </p>
                      <p className="text-xs mt-1" style={{ color: 'var(--theme-text-secondary)' }}>
                        Keep transfers distinct so analytics stay clean.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {step === 4 && (
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div
                      className="p-3 rounded-2xl"
                      style={{
                        backgroundColor: 'color-mix(in srgb, var(--theme-primary) 12%, transparent)',
                      }}
                    >
                      <BarChart3 className="h-5 w-5" style={{ color: 'var(--theme-primary)' }} />
                    </div>
                    <div>
                      <h2 className="text-xl md:text-2xl font-bold" style={{ color: 'var(--theme-text)' }}>
                        Review your spend insights on auto-pilot
                      </h2>
                    </div>
                  </div>


                  <div className="mt-3">
                        <p className="text-sm leading-relaxed" style={{ color: 'var(--theme-text-secondary)' }}>
                        Use Alto’s analytics, summary emails, and Alto AI will help you track your spending and stay on top of your finances.
                        </p>
                      </div>

                  <div className="grid md:grid-cols-3 gap-3">
                    <div className="apple-card !p-4">
                      <p className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
                        Analytics
                      </p>
                      <p className="text-xs mt-1" style={{ color: 'var(--theme-text-secondary)' }}>
                        Review spending trends and breakdowns by category.
                      </p>
                    </div>
                    <div className="apple-card !p-4">
                      <p className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--theme-text)' }}>
                        Weekly + monthly emails
                      </p>
                      <p className="text-xs mt-1" style={{ color: 'var(--theme-text-secondary)' }}>
                        Get weekly insights and monthly roundups delivered to your inbox.
                      </p>
                    </div>
                    <div className="apple-card !p-4">
                      <p className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
                        Alto AI assistant
                      </p>
                      <p className="text-xs mt-1" style={{ color: 'var(--theme-text-secondary)' }}>
                        Ask questions like “What were my biggest expenses?” and get answers instantly.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Footer nav */}
              <div className="mt-8 flex items-center justify-between gap-3">
                <Button
                  variant="outline"
                  onClick={prev}
                  disabled={step === 1}
                  className="rounded-xl gap-2"
                  style={{
                    backgroundColor: 'var(--theme-surface)',
                    borderColor: 'color-mix(in srgb, var(--theme-text-secondary) 20%, transparent)',
                    color: 'var(--theme-text)',
                  }}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Back
                </Button>

                {step < 4 ? (
                  <Button
                    onClick={next}
                    disabled={!canGoNext}
                    className="rounded-xl gap-2 bg-[var(--theme-primary)]"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleFinish}
                    disabled={isFinishing}
                    className="rounded-xl gap-2 bg-[var(--theme-primary)]"
                  >
                    {isFinishing ? 'Finishing…' : 'Finish onboarding'}
                    <CheckCircle2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="mt-4 text-xs text-center" style={{ color: 'var(--theme-text-secondary)' }}>
            You can revisit these steps any time from the Home page until onboarding is completed.
          </div>
        </div>
      </div>
    </div>
  );
};


